import { useEffect, useState } from 'react';
import { Sheet } from '../components/shared/Sheet';
import { Icon } from '../components/chrome/Icon';
import { useEncryption } from './EncryptionProvider';
import { useT } from '../i18n';

interface Props {
  open: boolean;
  intent: 'setup' | 'unlock';
  /** Called with true when ready to proceed, false when cancelled. */
  onResolved: (ok: boolean) => void;
}

const MIN_PW = 8;

/**
 * First-upload encryption setup (choose E2EE or not, then set a password) and
 * the new-device unlock prompt. Rendered by EncryptionProvider; consumers just
 * call ensureReady().
 */
export function EncryptionSheet({ open, intent, onResolved }: Props) {
  const t = useT();
  const { chooseNone, chooseEncrypted, unlock, busy, error, hint } = useEncryption();

  // setup sub-steps: 'choice' → 'password'
  const [step, setStep] = useState<'choice' | 'password'>('choice');
  const [pw, setPw] = useState('');
  const [pw2, setPw2] = useState('');
  const [hintText, setHintText] = useState('');
  const [wroteDown, setWroteDown] = useState(false);
  const [localErr, setLocalErr] = useState<string | null>(null);

  // Reset whenever (re)opened.
  useEffect(() => {
    if (open) {
      setStep('choice');
      setPw('');
      setPw2('');
      setHintText('');
      setWroteDown(false);
      setLocalErr(null);
    }
  }, [open, intent]);

  const close = () => onResolved(false);

  const onPickNone = async () => {
    try {
      await chooseNone();
      onResolved(true);
    } catch {
      /* error surfaced via context */
    }
  };

  const onSubmitPassword = async () => {
    setLocalErr(null);
    if (pw.length < MIN_PW) {
      setLocalErr(t('encryption.error.tooShort', 'Use at least {min} characters.').replace('{min}', String(MIN_PW)));
      return;
    }
    if (pw !== pw2) {
      setLocalErr(t('encryption.error.mismatch', 'The two passwords don’t match.'));
      return;
    }
    if (!wroteDown) {
      setLocalErr(t('encryption.error.notConfirmed', 'Please confirm you’ve written the password down.'));
      return;
    }
    try {
      await chooseEncrypted(pw, hintText.trim() || undefined);
      onResolved(true);
    } catch {
      /* error surfaced via context */
    }
  };

  const onSubmitUnlock = async () => {
    setLocalErr(null);
    const ok = await unlock(pw);
    if (ok) onResolved(true);
  };

  const label = intent === 'unlock'
    ? t('encryption.sheet.labelUnlock', 'Unlock your photos')
    : t('encryption.sheet.labelSetup', 'Protect your photos');

  return (
    <Sheet open={open} onClose={close} size="md" label={label}>
      <div className="flex flex-col gap-4 px-6 pb-8 pt-1">
        {/* ── UNLOCK ──────────────────────────────────────────────── */}
        {intent === 'unlock' && (
          <>
            <Header
              kicker={t('encryption.unlock.kicker', 'Encrypted')}
              title={t('encryption.unlock.title', 'Enter your photo password')}
              sub={t('encryption.unlock.sub', 'Your photos on this account are end-to-end encrypted. Enter the password you set to view and add photos on this device.')}
            />
            {hint && (
              <p className="rounded-xl bg-surface border border-line px-3.5 py-2.5 text-[12.5px] text-ink-2">
                {t('encryption.hintPrefix', 'Hint:')} <span className="text-ink">{hint}</span>
              </p>
            )}
            <PasswordField label={t('encryption.field.password', 'Password')} value={pw} onChange={setPw} onEnter={onSubmitUnlock} autoFocus />
            {(localErr || error) && <ErrorNote>{localErr ?? error}</ErrorNote>}
            <div className="mt-1 flex gap-2.5">
              <button onClick={close} className="min-h-12 rounded-3xl border border-line px-5 text-[14px] font-semibold text-ink">
                {t('common.cancel', 'Cancel')}
              </button>
              <button
                onClick={onSubmitUnlock}
                disabled={busy || pw.length === 0}
                className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-3xl bg-ink text-[14px] font-semibold text-bg disabled:opacity-60"
              >
                {busy ? t('encryption.unlock.busy', 'Unlocking…') : t('encryption.unlock.cta', 'Unlock')}
              </button>
            </div>
          </>
        )}

        {/* ── SETUP: choice ───────────────────────────────────────── */}
        {intent === 'setup' && step === 'choice' && (
          <>
            <Header
              kicker={t('encryption.choice.kicker', 'Your photos, your choice')}
              title={t('encryption.choice.title', 'Keep these photos private?')}
              sub={t('encryption.choice.sub', "You're about to upload a photo. Choose how it's stored — you can't change a photo's protection after it's uploaded.")}
            />

            <div className="rounded-2xl border border-line bg-surface p-4">
              <div className="flex items-center gap-2">
                <Icon name="check" size={15} color="var(--color-accent)" />
                <h3 className="font-serif text-[17px] tracking-tight text-ink">{t('encryption.choice.e2eeTitle', 'End-to-end encrypted')}</h3>
              </div>
              <ul className="mt-2 flex flex-col gap-1.5 text-[12.5px] leading-snug text-ink-2">
                <li>{t('encryption.choice.bullet1', '· Photos are scrambled on your device before upload — only you can open them.')}</li>
                <li>{t('encryption.choice.bullet2', '· Not even we (or anyone with server access) can see them.')}</li>
                <li>{t('encryption.choice.bullet3', '· Works across your phone and other devices once you enter your password.')}</li>
              </ul>
              <div
                className="mt-2.5 rounded-[10px] px-3 py-2.5 text-[12px] leading-snug"
                style={{ background: 'rgba(181, 82, 59, 0.10)', border: '1px solid rgba(181, 82, 59, 0.22)', color: '#7E3320' }}
              >
                <strong>{t('encryption.choice.importantLabel', 'Important:')}</strong> {t('encryption.choice.importantText', "we never see your password, so we can't reset it. If you forget it, these photos are lost permanently. Write it down somewhere safe.")}
              </div>
              <button
                onClick={() => setStep('password')}
                className="mt-3.5 flex min-h-12 w-full items-center justify-center gap-2 rounded-3xl bg-ink text-[14px] font-semibold text-bg"
              >
                {t('encryption.choice.setPasswordCta', 'Set a password & encrypt')}
              </button>
            </div>

            <div className="rounded-2xl border border-line bg-surface p-4">
              <h3 className="font-serif text-[17px] tracking-tight text-ink">{t('encryption.choice.noneTitle', 'Store without encryption')}</h3>
              <p className="mt-1.5 text-[12.5px] leading-snug text-ink-2">
                {t('encryption.choice.noneDesc', 'Simpler — no password to remember. Photos are stored normally on the server. Choose this if convenience matters more than maximum privacy.')}
              </p>
              <button
                onClick={onPickNone}
                disabled={busy}
                className="mt-3 flex min-h-11 w-full items-center justify-center rounded-3xl border border-line text-[14px] font-semibold text-ink disabled:opacity-60"
              >
                {busy ? t('common.saving', 'Saving…') : t('encryption.choice.noneCta', 'Continue without encryption')}
              </button>
            </div>

            {error && <ErrorNote>{error}</ErrorNote>}
            <button onClick={close} className="mx-auto mt-1 text-[13px] font-medium text-ink-3">
              {t('encryption.choice.notNow', 'Not now')}
            </button>
          </>
        )}

        {/* ── SETUP: password ─────────────────────────────────────── */}
        {intent === 'setup' && step === 'password' && (
          <>
            <Header
              kicker={t('encryption.setup.kicker', 'End-to-end encryption')}
              title={t('encryption.setup.title', 'Choose your photo password')}
              sub={t('encryption.setup.sub', 'This password encrypts your photos. We never receive it — keep it safe.')}
            />
            <PasswordField label={t('encryption.field.password', 'Password')} value={pw} onChange={setPw} autoFocus />
            <PasswordField label={t('encryption.field.confirmPassword', 'Confirm password')} value={pw2} onChange={setPw2} onEnter={onSubmitPassword} />
            <div>
              <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-ink-3">
                {t('encryption.field.hintLabel', 'Hint')} <span className="text-ink-3/70">{t('encryption.field.hintOptional', '· optional, stored unencrypted')}</span>
              </label>
              <input
                value={hintText}
                onChange={(e) => setHintText(e.target.value)}
                placeholder={t('encryption.field.hintPlaceholder', 'e.g. our usual passphrase')}
                className="min-h-11 w-full rounded-xl border border-line bg-bg px-3.5 text-[14px] text-ink outline-none focus:border-accent"
              />
            </div>

            <label className="flex items-start gap-2.5 rounded-xl bg-surface border border-line px-3.5 py-3 text-[12.5px] leading-snug text-ink-2">
              <input
                type="checkbox"
                checked={wroteDown}
                onChange={(e) => setWroteDown(e.target.checked)}
                className="mt-0.5 size-4 shrink-0 accent-[var(--color-accent)]"
              />
              <span>
                {t('encryption.setup.confirmPrefix', 'I understand that if I lose this password,')}{' '}
                <span className="font-semibold text-ink">{t('encryption.setup.confirmBold', "my encrypted photos can't be recovered")}</span>.
                {' '}{t('encryption.setup.confirmSuffix', "I've written it down somewhere safe.")}
              </span>
            </label>

            {(localErr || error) && <ErrorNote>{localErr ?? error}</ErrorNote>}

            <div className="mt-1 flex gap-2.5">
              <button onClick={() => setStep('choice')} className="min-h-12 rounded-3xl border border-line px-5 text-[14px] font-semibold text-ink">
                {t('common.back', 'Back')}
              </button>
              <button
                onClick={onSubmitPassword}
                disabled={busy}
                className="flex min-h-12 flex-1 items-center justify-center gap-2 rounded-3xl bg-ink text-[14px] font-semibold text-bg disabled:opacity-60"
              >
                <Icon name="check" size={15} color="currentColor" />
                {busy ? t('encryption.setup.busy', 'Encrypting…') : t('encryption.setup.cta', 'Enable encryption')}
              </button>
            </div>
          </>
        )}
      </div>
    </Sheet>
  );
}

/* ── Small building blocks ───────────────────────────────────────────── */

function Header({ kicker, title, sub }: { kicker: string; title: string; sub: string }) {
  return (
    <div>
      <p className="text-[11.5px] font-semibold uppercase tracking-[0.12em] text-ink-3">{kicker}</p>
      <h2 className="mt-1 font-serif text-2xl leading-snug tracking-tight text-ink text-pretty">{title}</h2>
      <p className="mt-1.5 text-[13px] leading-relaxed text-ink-2 text-pretty">{sub}</p>
    </div>
  );
}

function PasswordField({
  label, value, onChange, onEnter, autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  onEnter?: () => void;
  autoFocus?: boolean;
}) {
  const t = useT();
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-ink-3">{label}</label>
      <div className="flex min-h-12 items-center gap-2 rounded-xl border border-line bg-bg px-3.5 focus-within:border-accent">
        <input
          type={show ? 'text' : 'password'}
          value={value}
          autoFocus={autoFocus}
          onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && onEnter) onEnter(); }}
          className="min-w-0 flex-1 bg-transparent py-3 text-[15px] text-ink outline-none"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="shrink-0 text-[12px] font-semibold text-ink-3"
        >
          {show ? t('common.hide', 'Hide') : t('common.show', 'Show')}
        </button>
      </div>
    </div>
  );
}

function ErrorNote({ children }: { children: ReactNodeLike }) {
  return (
    <p
      className="rounded-[10px] px-3 py-2.5 text-[12.5px] leading-snug"
      style={{ background: 'rgba(181, 82, 59, 0.10)', border: '1px solid rgba(181, 82, 59, 0.22)', color: '#7E3320' }}
    >
      {children}
    </p>
  );
}

type ReactNodeLike = string | null | undefined;
